import { excursionsService, accommodationsService } from '@/api/services';
import { useExcursionsList } from '@/hooks';
import { getExcursionImage, toImageSrc } from '@/utils/imageUrl';
import { collectLicensedGuideNames } from '@/utils/licensedGuideNames';
import { fetchLowestFromPriceForExcursion, getFromPriceYear } from '@/utils/excursionFromPrice';
import { getSchedulesForDate, getSeatsLeftUrgency, normalizeSalesCapacityRow } from '@/utils/excursionAvailability';
import { scheduleMatchesLanguage } from '@/utils/excursionLanguageFilter';
import { Col, Container, Row, Spinner } from 'react-bootstrap';
import { useSearchParams } from '@/compat/react-router-dom';
import { useEffect, useState, useMemo, useRef } from 'react';
import TourCard from './TourCard';
import tour1 from '@/assets/images/category/tour/01.jpg';
import tour2 from '@/assets/images/category/tour/02.jpg';
import tour3 from '@/assets/images/category/tour/03.jpg';
import tour4 from '@/assets/images/category/tour/04.jpg';
import tour5 from '@/assets/images/category/tour/05.jpg';
import tour6 from '@/assets/images/category/tour/06.jpg';
import deFlag from '@/assets/images/flags/de.svg';
import frFlag from '@/assets/images/flags/fr.svg';
import ukFlag from '@/assets/images/flags/uk.svg';
import czFlag from '@/assets/images/flags/cz.svg';
import plFlag from '@/assets/images/flags/pl.svg';

// Fallback images array (using available images)
const tourImages = [tour1, tour2, tour3, tour4, tour5, tour6].map((img) => toImageSrc(img)).filter(Boolean);

const languageFlagMap = {
  de: deFlag, en: ukFlag, gb: ukFlag, fr: frFlag, cz: czFlag, pl: plFlag, el: ukFlag, gr: ukFlag
};
const parseYmdDate = (value) => {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(0, 0, 0, 0);
  return parsed;
};

const formatSearchRangeLabel = (from, to) => {
  if (!from) return '';
  const month = (date) => date.toLocaleString('en-GB', { month: 'short' });
  if (!to || from.getTime() === to.getTime()) {
    return `${from.getDate()} ${month(from)}`;
  }
  if (from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear()) {
    return `${from.getDate()}–${to.getDate()} ${month(to)}`;
  }
  return `${from.getDate()} ${month(from)} – ${to.getDate()} ${month(to)}`;
};

const isExcursionOnline = (excursion) =>
  !(excursion?.is_online === 0 || excursion?.is_online === '0');

const toSsrTourCard = (excursion, idx) => {
  const fallbackImage = tourImages[idx % tourImages.length];
  let image = fallbackImage;
  if (excursion.images && Array.isArray(excursion.images) && excursion.images.length > 0) {
    const first = excursion.images[0];
    const path = first?.image_path || first?.file_path || first?.file_url || first?.url;
    if (path) image = getExcursionImage(path, fallbackImage);
  }
  return {
    id: excursion.id,
    name: excursion.name || 'Tour Name',
    type: excursion.type || 'Excursion',
    typeIcon: excursion.icon || null,
    image,
    days: 1,
    nights: 0,
    datePrefix: null,
    dateValue: null,
    daysOfWeekText: null,
    price: excursion.price ?? excursion.starting_price ?? null,
    pricingUnit: 'person',
    groupMinimumAdults: null,
    durationText: null,
    pickupAvailable: false,
    pickupRegionNames: [],
    startRegionName: excursion.start_region_name || null,
    lunchIncludedText: null,
    wineOrBeerIncludedText: null,
    languages: [],
    tourGuideLanguages: [],
    licensedGuideNames: [],
    description: excursion.description,
    itinerary: excursion.itinerary,
    highlights: excursion.highlights || [],
    inclusions: excursion.inclusions || [],
    exclusions: excursion.exclusions || [],
    schedules: excursion.schedules || [],
    slug: excursion.slug,
    tagline: excursion.tagline || null,
  };
};

const TourCardList = ({ excursionBasePath = '/excursions' }) => {
  const [searchParams] = useSearchParams();
  const { data: excursionsData, loading: excursionsLoading, error: excursionsError } = useExcursionsList();
  const [excursions, setExcursions] = useState(() => {
    if (!Array.isArray(excursionsData)) return [];
    return excursionsData.filter(isExcursionOnline).map(toSsrTourCard);
  });
  const [loading, setLoading] = useState(() => !Array.isArray(excursionsData));
  const [error, setError] = useState(null);
  const pricelistCacheRef = useRef(new Map());
  const precisePriceCacheRef = useRef(new Map());
  const [pickups, setPickups] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [remainingSeatsByExcId, setRemainingSeatsByExcId] = useState({});

  // Get filter criteria from URL params
  const filters = useMemo(() => ({
    date: searchParams.get('date') || null,
    dateFrom: searchParams.get('dateFrom') || null,
    dateTo: searchParams.get('dateTo') || null,
    language: searchParams.get('language') || null,
    duration: searchParams.get('duration') || null,
    pickupArea: searchParams.get('pickupArea') || null,
    afternoon: searchParams.get('afternoon') || null,
  }), [searchParams]);

  useEffect(() => {
    const capacityDay = filters.date || filters.dateFrom;
    if (!capacityDay) {
      setRemainingSeatsByExcId({});
      return undefined;
    }
    let cancelled = false;
    excursionsService
      .getSalesCapacity({
        date_from: capacityDay,
        date_to: capacityDay,
      })
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
        const minByExcId = {};
        rows.forEach((row) => {
          const excId = Number(row?.exc_id || row?.excursion_id || 0);
          const normalized = normalizeSalesCapacityRow(row, capacityDay);
          if (!excId || normalized?.remainingSeats == null) return;
          const current = minByExcId[excId];
          if (current == null || normalized.remainingSeats < current) {
            minByExcId[excId] = normalized.remainingSeats;
          }
        });
        setRemainingSeatsByExcId(minByExcId);
      })
      .catch(() => {
        if (!cancelled) setRemainingSeatsByExcId({});
      });
    return () => {
      cancelled = true;
    };
  }, [filters.date, filters.dateFrom]);

  // Fetch pickups and hotels for filtering
  useEffect(() => {
    const fetchPickupsAndHotels = async () => {
      try {
        const [pickupsResponse, hotelsResponse] = await Promise.all([
          excursionsService.getAllPickups().catch(() => ({ data: { data: [] } })),
          accommodationsService.getAll().catch(() => ({ data: { data: [] } }))
        ]);
        setPickups(pickupsResponse.data?.data || pickupsResponse.data || []);
        const hotelsData = hotelsResponse.data?.data || hotelsResponse.data || [];
        setHotels(hotelsData);
      } catch {
        setPickups([]);
        setHotels([]);
      }
    };
    fetchPickupsAndHotels();
  }, []);

  useEffect(() => {
    // Pickups may load after initial render; clear stale price cache so region-based reductions are recalculated.
    precisePriceCacheRef.current.clear();
  }, [pickups]);

  useEffect(() => {
    if (excursionsLoading || excursionsData == null) {
      if (excursions.length === 0) {
        setLoading(true);
      }
      return;
    }

    if (excursionsError) {
      const message =
        excursionsError?.response?.data?.message ||
        excursionsError?.message ||
        'Failed to load excursions.';
      setError(message);
      setExcursions([]);
      setLoading(false);
      return;
    }

    const excursionsList = Array.isArray(excursionsData) ? excursionsData : [];
    if (excursionsList.length === 0) {
      setExcursions([]);
      setLoading(false);
      return;
    }

    const getScheduleYear = (excursion) => {
      const dateValue =
        excursion?.next_schedule?.start_date ||
        excursion?.schedules?.[0]?.start_date ||
        null;
      if (!dateValue) return new Date().getFullYear();
      const parsed = new Date(dateValue);
      if (Number.isNaN(parsed.getTime())) return new Date().getFullYear();
      return parsed.getFullYear();
    };

    const visibleExcursions = excursionsList.filter(isExcursionOnline);
    const years = Array.from(
      new Set(visibleExcursions.map(getScheduleYear).filter(Boolean))
    );

    const buildPriceByYear = () => {
      const priceByYear = {};
      years.forEach((year) => {
        priceByYear[year] = pricelistCacheRef.current.get(year) || {};
      });
      return priceByYear;
    };

    const buildTransformed = (priceByYear) => {
      return visibleExcursions.map((excursion, idx) => {
        const fallbackImage = tourImages[idx % tourImages.length];
        let image = fallbackImage;
        // Prefer first main picture (Media tab) when set
        if (excursion.images && Array.isArray(excursion.images) && excursion.images.length > 0) {
          const first = excursion.images[0];
          const path = first?.image_path || first?.file_path || first?.file_url || first?.url;
          if (path) image = getExcursionImage(path, fallbackImage);
        }
        if (image === fallbackImage && excursion.highlights && excursion.highlights.length > 0) {
          const firstHighlight = excursion.highlights[0];
          if (firstHighlight.images && firstHighlight.images.length > 0) {
            image = getExcursionImage(firstHighlight.images[0], fallbackImage);
          }
        }

        const nextSchedule = excursion.next_schedule;
        const formatMonthDay = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        let datePrefix = null;
        let dateValue = null;
        if (nextSchedule?.start_date) {
          const startDate = new Date(nextSchedule.start_date);
          startDate.setHours(0, 0, 0, 0);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const endDate = nextSchedule?.end_date ? new Date(nextSchedule.end_date) : null;
          if (endDate && today > startDate) {
            datePrefix = 'Until';
            dateValue = formatMonthDay(endDate);
          } else {
            datePrefix = 'From';
            dateValue = formatMonthDay(startDate);
          }
        }
        // days_of_week: 7-char string per schedule (0=Mon..6=Sun, '1'=runs that day). Aggregate across ALL schedules so we show every day the excursion runs.
        const dayNamesFull = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        let daysOfWeekText = null;
        const allSchedules = excursion.schedules ?? (nextSchedule ? [nextSchedule] : []);
        const activeDaySet = new Set();
        for (const s of allSchedules) {
          const dow = s?.days_of_week;
          if (!dow) continue;
          const str = typeof dow === 'string' ? dow : Array.isArray(dow) ? dow.map((x) => (x === 1 || x === '1' ? '1' : '0')).join('') : '';
          if (str.length >= 7) {
            for (let i = 0; i < 7; i++) {
              if (str[i] === '1') activeDaySet.add(dayNamesFull[i]);
            }
          }
        }
        if (activeDaySet.size === 7) {
          daysOfWeekText = 'Every day';
        } else if (activeDaySet.size > 0) {
          const active = dayNamesFull.filter((d) => activeDaySet.has(d));
          const dayPart = active.length === 1
            ? active[0]
            : `${active.slice(0, -1).join(', ')} & ${active[active.length - 1]}`;
          daysOfWeekText = `Every ${dayPart}`;
        }

        const scheduleYear = getScheduleYear(excursion);
        const priceEntry = priceByYear[scheduleYear]?.[String(excursion.id)] ?? null;
        const price = (priceEntry && typeof priceEntry === 'object' ? priceEntry.price : priceEntry) ?? excursion.price ?? excursion.starting_price ?? null;
        const fromMinimumAdults = Number(priceEntry?.minimumAdults ?? 1) || 1;
        const nextScheduleMinimumAdults = Number(nextSchedule?.minimum_adults ?? 1);
        const schedulesForUnit = Array.isArray(excursion.schedules) ? excursion.schedules : [];
        const hasGroupSchedule = schedulesForUnit.some((s) => Number(s?.minimum_adults ?? 1) > 1);
        const hasPerPersonSchedule = schedulesForUnit.some((s) => Number(s?.minimum_adults ?? 1) <= 1);
        const pricingUnit = fromMinimumAdults > 1
          ? "group"
          : (
            nextSchedule
              ? (nextScheduleMinimumAdults > 1 ? "group" : "person")
              : (hasGroupSchedule && !hasPerPersonSchedule ? "group" : "person")
          );
        const groupMinimumAdults = pricingUnit === "group"
          ? (fromMinimumAdults > 1 ? fromMinimumAdults : (nextScheduleMinimumAdults > 1 ? nextScheduleMinimumAdults : null))
          : null;

        // Duration: from excursion.duration (hours) or sum of highlight durations (minutes → hours)
        let durationHours = null;
        if (excursion.duration != null && excursion.duration !== '') {
          const d = Number(excursion.duration);
          durationHours = d < 24 ? d : d / 60;
        } else if (excursion.highlights && excursion.highlights.length > 0) {
          const totalMins = excursion.highlights.reduce((sum, h) => sum + (Number(h.duration) || 0), 0);
          if (totalMins > 0) durationHours = totalMins / 60;
        }
        const durationText = durationHours != null && durationHours > 0
          ? `${durationHours % 1 === 0 ? durationHours : durationHours.toFixed(1)} ${durationHours === 1 ? 'hour' : 'hours'}`
          : null;

        const pickupAvailable = !!(excursion.pickup_available === true || excursion.pickup_available === 1 || excursion.pickup_available === '1' || Number(excursion.pickup_available) === 1);
        const startRegionName = excursion.start_region_name || null;

        // Pickup regions: unique region names from pickups for this excursion, ordered by region order ASC
        const pickupRegionNames = (() => {
          if (!pickupAvailable || !Array.isArray(pickups) || pickups.length === 0) return [];
          const excPickups = pickups.filter((p) => Number(p.exc_id) === Number(excursion.id));
          const byRegion = new Map();
          excPickups.forEach((p) => {
            const name = p.region_name || p.region?.name || null;
            if (!name) return;
            const order = p.region_order != null ? Number(p.region_order) : 9999;
            if (!byRegion.has(name)) byRegion.set(name, order);
          });
          return Array.from(byRegion.entries())
            .sort((a, b) => a[1] - b[1])
            .map(([name]) => name);
        })();

        // Unique languages from schedules (for language filter / guide badges)
        const langMap = new Map();
        const tourGuideLangMap = new Map();
        (excursion.schedules || []).forEach((s) => {
          const rawCode = (s.language_code || '').toLowerCase().trim();
          const code = rawCode === 'gb' ? 'en' : rawCode;
          const name = s.language_name || s.language_code || null;
          const displayCode = (code || (name ? name.slice(0, 2) : '')).toUpperCase();
          const flag = languageFlagMap[code] || ukFlag;
          if (displayCode || name) langMap.set(displayCode, { code: displayCode, name: name || displayCode, flag });

          const isGuidedTour = s?.is_guided_tour === 1 || s?.is_guided_tour === true || s?.is_guided_tour === '1';
          if (isGuidedTour && (displayCode || name)) {
            tourGuideLangMap.set(displayCode, { code: displayCode, name: name || displayCode, flag });
          }
        });
        const languages = Array.from(langMap.values());
        const tourGuideLanguages = Array.from(tourGuideLangMap.values());
        const licensedGuideNames = collectLicensedGuideNames(excursion.schedules || []);

        return {
          id: excursion.id,
          name: excursion.name || 'Tour Name',
          type: excursion.type || 'Excursion',
          typeIcon: excursion.icon || null,
          image: image,
          days: 1,
          nights: 0,
          datePrefix,
          dateValue,
          daysOfWeekText,
          price,
          pricingUnit,
          groupMinimumAdults,
          durationText,
          pickupAvailable,
          pickupRegionNames,
          startRegionName,
          lunchIncludedText: (excursion.inclusions || []).find(
            (i) => i && String(i).toLowerCase().includes('lunch included')
          ) ?? null,
          wineOrBeerIncludedText: (excursion.inclusions || []).find(
            (i) => i && (String(i).toLowerCase().includes('wine') || String(i).toLowerCase().includes('beer'))
          ) ?? null,
          languages,
          tourGuideLanguages,
          licensedGuideNames,
          description: excursion.description,
          itinerary: excursion.itinerary,
          highlights: excursion.highlights || [],
          inclusions: excursion.inclusions || [],
          exclusions: excursion.exclusions || [],
          schedules: excursion.schedules || [],
          slug: excursion.slug,
          tagline: excursion.tagline || null,
        };
      });
    };

    const applyFilters = (list) => {
      let filteredExcursions = list;

      const selectedFromDate = parseYmdDate(filters.dateFrom || filters.date);
      const selectedToDate = parseYmdDate(filters.dateTo || filters.date || filters.dateFrom);
      if (selectedFromDate || selectedToDate) {
        const startDate = selectedFromDate || selectedToDate;
        const endDate = selectedToDate || selectedFromDate;
        const rangeStart = startDate <= endDate ? startDate : endDate;
        const rangeEnd = startDate <= endDate ? endDate : startDate;

        filteredExcursions = filteredExcursions.filter((excursion) => {
          const schedules = Array.isArray(excursion.schedules) ? excursion.schedules : [];
          if (schedules.length === 0) return false;

          const currentDate = new Date(rangeStart);
          while (currentDate <= rangeEnd) {
            const daySchedules = getSchedulesForDate(schedules, currentDate)
              .filter((schedule) => scheduleMatchesLanguage(schedule, filters.language));
            if (daySchedules.length > 0) {
              return true;
            }
            currentDate.setDate(currentDate.getDate() + 1);
          }
          return false;
        });
      } else if (filters.language) {
        filteredExcursions = filteredExcursions.filter((excursion) => {
          if (!excursion.schedules || excursion.schedules.length === 0) {
            return false;
          }
          return excursion.schedules.some((schedule) => scheduleMatchesLanguage(schedule, filters.language));
        });
      }

      if (filters.pickupArea) {
        const selectedRegionId = String(filters.pickupArea);
        const hotelIdsInRegion = hotels
          .filter((hotel) => {
            const hotelRegionId = String(
              hotel.region_id ||
                hotel.destination_region_id ||
                hotel.region?.id ||
                hotel.destination?.region_id ||
                ''
            );
            return hotelRegionId === selectedRegionId;
          })
          .map((hotel) => String(hotel.id));

        const scheduleIdsByLanguage = new Map();
        if (filters.language) {
          filteredExcursions.forEach((excursion) => {
            if (excursion.schedules && excursion.schedules.length > 0) {
              const matchingScheduleIds = excursion.schedules
                .filter((schedule) => scheduleMatchesLanguage(schedule, filters.language))
                .map((schedule) => String(schedule.id));
              if (matchingScheduleIds.length > 0) {
                scheduleIdsByLanguage.set(String(excursion.id), matchingScheduleIds);
              }
            }
          });
        }

        const excursionIdsWithPickupsInRegion = new Set(
          pickups
            .filter((pickup) => {
              const pickupHotelId = String(
                pickup.acc_id || pickup.accommodation_id || pickup.hotel_id || ''
              );
              const isHotelInRegion = hotelIdsInRegion.includes(pickupHotelId);
              if (!isHotelInRegion) return false;

              if (filters.language && scheduleIdsByLanguage.size > 0) {
                const excursionId = String(pickup.exc_id || pickup.excursion_id || '');
                const matchingScheduleIds = scheduleIdsByLanguage.get(excursionId);
                if (!matchingScheduleIds || matchingScheduleIds.length === 0) return false;

                const pickupScheduleId = String(pickup.schedule_id || '');
                return matchingScheduleIds.includes(pickupScheduleId);
              }

              return true;
            })
            .map((pickup) => String(pickup.exc_id || pickup.excursion_id || ''))
            .filter((id) => id && id !== '')
        );

        filteredExcursions = filteredExcursions.filter((excursion) => {
          const excursionId = String(excursion.id);
          return excursionIdsWithPickupsInRegion.has(excursionId);
        });
      }

      return filteredExcursions;
    };

    const fetchPreciseFromPrice = async (excursion) => {
      if (!excursion?.id) return null;
      const year = getFromPriceYear(excursion?.schedules ?? []);
      const cacheKey = `${year}:${excursion.id}`;
      const hasInlinePickups = Array.isArray(excursion?.pickups) && excursion.pickups.length > 0;
      const hasFallbackPickups = Array.isArray(pickups) && pickups.length > 0;
      const canCache = hasInlinePickups || hasFallbackPickups;
      if (canCache && precisePriceCacheRef.current.has(cacheKey)) {
        return precisePriceCacheRef.current.get(cacheKey);
      }
      const best = await fetchLowestFromPriceForExcursion({
        excursion,
        fallbackPickups: pickups,
        getPricelists: (yearArg, excursionId, regionId) => excursionsService.getPricelists(yearArg, excursionId, regionId),
      });
      if (!best) {
        if (canCache) precisePriceCacheRef.current.set(cacheKey, null);
        return null;
      }
      if (canCache) precisePriceCacheRef.current.set(cacheKey, best);
      return best;
    };

    let cancelled = false;
    const baseTransformed = buildTransformed(buildPriceByYear());
    const filteredBase = applyFilters(baseTransformed);
    setExcursions(filteredBase);
    Promise.allSettled(filteredBase.map((exc) => fetchPreciseFromPrice(exc)))
      .then((results) => {
        if (cancelled) return;
        const preciseById = {};
        results.forEach((result, idx) => {
          if (result.status !== 'fulfilled' || !result.value) return;
          preciseById[String(filteredBase[idx].id)] = result.value;
        });
        if (!Object.keys(preciseById).length) return;
        setExcursions((prev) => prev.map((tour) => {
          const precise = preciseById[String(tour.id)];
          if (!precise) return tour;
          const minimumAdults = Math.max(1, Number(precise.minimumAdults || 1) || 1);
          return {
            ...tour,
            price: precise.price,
            pricingUnit: minimumAdults > 1 ? 'group' : 'person',
            groupMinimumAdults: minimumAdults > 1 ? minimumAdults : null,
          };
        }));
      })
      .catch(() => {});
    setError(null);
    setLoading(false);

    return () => {
      cancelled = true;
    };
  }, [
    excursionsData,
    excursionsLoading,
    excursionsError,
    excursions.length,
    filters.date,
    filters.dateFrom,
    filters.dateTo,
    filters.language,
    filters.duration,
    filters.pickupArea,
    filters.afternoon,
    pickups,
    hotels
  ]);

  if (loading && excursions.length === 0) {
    return (
      <section className="excursions-results-section bg-white">
        <Container>
          <Row>
            <Col xs={12} className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading excursions...</p>
            </Col>
          </Row>
        </Container>
      </section>
    );
  }

  if (error && excursions.length === 0) {
    return (
      <section className="excursions-results-section bg-white">
        <Container>
          <Row>
            <Col xs={12} className="text-center py-5">
              <div className="alert alert-warning" role="alert">
                <h5 className="alert-heading">Error Loading Excursions</h5>
                <p>{error}</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    );
  }

  const rangeFrom = parseYmdDate(filters.dateFrom || filters.date);
  const rangeTo = parseYmdDate(filters.dateTo || filters.date || filters.dateFrom);
  const rangeLabel = formatSearchRangeLabel(rangeFrom, rangeTo);
  const resultsCountLabel = rangeLabel
    ? excursions.length === 1
      ? `1 tour available ${rangeLabel}`
      : `${excursions.length} tours available ${rangeLabel}`
    : excursions.length === 1
      ? '1 tour available'
      : `${excursions.length} tours available`;

  return (
    <section className="tour-results-section excursions-results-section bg-white">
      <Container>
        <p className="excursions-results-count">{resultsCountLabel}</p>
        {excursions.length > 0 ? (
          <Row className="g-4">
            {excursions.map((tour, idx) => (
              <Col md={6} xl={4} key={tour.id || idx}>
                      <TourCard
                        tour={tour}
                        excursionBasePath={excursionBasePath}
                        urgency={getSeatsLeftUrgency(remainingSeatsByExcId[Number(tour.id)] ?? remainingSeatsByExcId[String(tour.id)])}
                      />
              </Col>
            ))}
          </Row>
        ) : (
          <Row>
            <Col xs={12} className="text-center py-5">
              <p className="text-muted">
                {rangeLabel ? `No tours available ${rangeLabel}. Try another date range.` : 'No excursions available at the moment.'}
              </p>
            </Col>
          </Row>
        )}
      </Container>
    </section>
  );
};

export default TourCardList;
